import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatTimestamp, formatRelativeTime } from '@/lib/formatters';
import type { AdminUserProjectsResponse } from '@/types/admin';

export default function UserProjectsPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<AdminUserProjectsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetch() {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch(`users/${userId}/projects`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
        setData((await res.json()) as AdminUserProjectsResponse);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : 'Failed to load projects');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetch();
    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  const totalDocs = data?.projects.reduce((s, p) => s + p.documentCount, 0) ?? 0;

  return (
    <>
      <LoadingOverlay visible={loading} message="Loading projects..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Projects' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/users/${userId}/documents`}>Documents →</Link>
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Projects (projects-table)</CardTitle>
            <CardDescription>
              {data
                ? `${data.projects.length} projects · ${totalDocs} documents total`
                : 'User writing projects'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data && data.projects.length > 0 ? (
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project ID</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Verification</TableHead>
                      <TableHead className="text-right">Docs</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell className="font-mono text-xs">{project.id}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{project.title}</span>
                            {project.context && (
                              <span className="text-xs text-muted-foreground line-clamp-2">{project.context}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{project.author ?? '—'}</TableCell>
                        <TableCell>
                          {project.verifyPending ? (
                            <Badge variant="outline">Pending</Badge>
                          ) : project.verified === true ? (
                            <Badge variant="default">Verified</Badge>
                          ) : project.verified === false ? (
                            <Badge variant="destructive">Rejected</Badge>
                          ) : (
                            '—'
                          )}
                          {project.verificationComment && (
                            <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
                              {project.verificationComment}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {project.documentCount}
                        </TableCell>
                        <TableCell className="text-xs">
                          {project.created ? formatTimestamp(project.created) : '—'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {project.updated ? (
                            <span title={formatTimestamp(project.updated)}>{formatRelativeTime(project.updated)}</span>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={4}>Total</TableCell>
                      <TableCell className="text-right font-mono">{totalDocs}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            ) : (
              !loading && <p className="text-sm text-muted-foreground">No projects found.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
